import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["panel", "items", "toggle", "overlay"]
    static values = { page: String }

    connect() {
        this.isExpanded = this.loadExpandedState()
        this.helpItems = []
        this.currentHighlight = null

        // Apply initial state
        this.updatePanelState()

        // Load help items
        this.loadHelpItems()

        // Set up focus listeners for existing fields
        this.setupFocusListeners()

        // Watch for dynamically added fields (nested forms)
        this.setupMutationObserver()

        // Handle Turbo navigation
        document.addEventListener('turbo:before-visit', this.handleTurboNavigation.bind(this))
    }

    disconnect() {
        if (this.observer) {
            this.observer.disconnect()
        }
        document.removeEventListener('turbo:before-visit', this.handleTurboNavigation.bind(this))
    }

    toggle() {
        this.isExpanded = !this.isExpanded
        this.saveExpandedState()
        this.updatePanelState()
    }

    close() {
        this.isExpanded = false
        this.saveExpandedState()
        this.updatePanelState()
    }

    closeOnOverlay(event) {
        // Only close if clicking the overlay itself, not the panel
        if (event.target === this.overlayTarget) {
            this.close()
        }
    }

    updatePanelState() {
        if (this.isExpanded) {
            this.element.classList.add('help-panel--expanded')
            this.element.classList.remove('help-panel--collapsed')
            if (this.hasOverlayTarget) {
                this.overlayTarget.classList.add('help-panel-overlay--visible')
            }
        } else {
            this.element.classList.remove('help-panel--expanded')
            this.element.classList.add('help-panel--collapsed')
            if (this.hasOverlayTarget) {
                this.overlayTarget.classList.remove('help-panel-overlay--visible')
            }
        }

        // Update toggle button icon
        if (this.hasToggleTarget) {
            const icon = this.toggleTarget.querySelector('i')
            if (icon) {
                icon.className = this.isExpanded ? 'fas fa-chevron-right' : 'fas fa-question-circle'
            }
        }
    }

    loadExpandedState() {
        return false
    }

    saveExpandedState() {
        // intentionally not persisted — panel always starts closed
    }

    async loadHelpItems() {
        if (!this.pageValue) return

        this.showLoading()

        try {
            const response = await fetch(`/help_items/find_page.json?p=${encodeURIComponent(this.pageValue)}`, {
                headers: {
                    'Accept': 'application/json'
                }
            })

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`)
            }

            const data = await response.json()
            this.helpItems = data
            this.renderHelpItems()
        } catch (error) {
            console.error('Failed to load help items:', error)
            this.showError()
        }
    }

    showLoading() {
        if (this.hasItemsTarget) {
            this.itemsTarget.innerHTML = `
                <div class="help-panel__loading">
                    <i class="fas fa-spinner fa-spin"></i>
                    <span>Loading help...</span>
                </div>
            `
        }
    }

    showError() {
        if (this.hasItemsTarget) {
            this.itemsTarget.innerHTML = `
                <div class="help-panel__error">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>Failed to load help items</span>
                    <button class="btn btn-sm btn-outline-secondary mt-2" data-action="click->help-panel#loadHelpItems">
                        Retry
                    </button>
                </div>
            `
        }
    }

    showEmpty() {
        if (this.hasItemsTarget) {
            this.itemsTarget.innerHTML = `
                <div class="help-panel__empty">
                    <i class="fas fa-info-circle"></i>
                    <span>No help available for this page</span>
                </div>
            `
        }
    }

    renderHelpItems() {
        if (!this.hasItemsTarget) return

        if (!this.helpItems || this.helpItems.length === 0) {
            this.showEmpty()
            return
        }

        const itemsHtml = this.helpItems.map(item => {
            const reference = item.reference || ''
            return `
                <div class="help-panel__item" data-help-reference="${this.escapeHtml(reference)}">
                    <div class="help-panel__item-header">
                        ${item.header || ''}
                    </div>
                    <div class="help-panel__item-body">
                        ${item.body || ''}
                    </div>
                    ${item.footer ? `<div class="help-panel__item-footer">${item.footer}</div>` : ''}
                </div>
            `
        }).join('')

        this.itemsTarget.innerHTML = itemsHtml
    }

    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    setupFocusListeners() {
        const fields = this.element.querySelectorAll('[data-help-reference]')
        fields.forEach(field => this.addFocusListener(field))
    }

    addFocusListener(field) {
        // Skip if already has listener
        if (field.dataset.helpListenerAttached) return

        field.addEventListener('focus', (event) => {
            const reference = event.target.dataset.helpReference
            if (reference) {
                this.highlightItem(reference)
            }
        })

        field.addEventListener('blur', () => {
            // Delay removal to allow clicking on help item
            setTimeout(() => this.clearHighlight(), 200)
        })

        field.dataset.helpListenerAttached = 'true'
    }

    setupMutationObserver() {
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Check if the added node has help-reference
                        if (node.dataset && node.dataset.helpReference) {
                            this.addFocusListener(node)
                        }
                        // Check children of added node
                        const fields = node.querySelectorAll ? node.querySelectorAll('[data-help-reference]') : []
                        fields.forEach(field => this.addFocusListener(field))
                    }
                })
            })
        })

        this.observer.observe(this.element, {
            childList: true,
            subtree: true
        })
    }

    highlightItem(reference) {
        // Clear previous highlight
        this.clearHighlight()

        // Find matching help item
        const item = this.itemsTarget?.querySelector(`[data-help-reference="${reference}"]`)
        if (item) {
            item.classList.add('help-panel__item--highlighted')
            this.currentHighlight = item

            // Only scroll to the item if the panel is already open.
            // Focusing a form field must NOT force-open the panel —
            // only the help bubble (toggle) should open it.
            if (this.isExpanded) {
                item.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            }
        }
    }

    clearHighlight() {
        if (this.currentHighlight) {
            this.currentHighlight.classList.remove('help-panel__item--highlighted')
            this.currentHighlight = null
        }
    }

    handleTurboNavigation() {
        // Clean up before Turbo navigates away
        this.clearHighlight()
    }
}
